<script setup lang="ts">
import type { DailyStatistic } from '@features/home/types'

type StatisticCardProps = {
  statistic: DailyStatistic
  index: number
}

const props = defineProps<StatisticCardProps>()

const digitValue = ref(0)
const titleLines = computed(() => props.statistic.title.split(/<br\s*\/?>/i))
let animationFrameId: number | undefined

const animate = (initValue: number, lastValue: number, duration: number) => {
  if (import.meta.server) return

  let startTime = 0

  const step = (currentTime: number) => {
    if (!startTime) {
      startTime = currentTime
    }

    const progress = Math.min((currentTime - startTime) / duration, 1)

    digitValue.value = Math.floor(
      progress * (lastValue - initValue) + initValue,
    )

    if (progress < 1) {
      animationFrameId = window.requestAnimationFrame(step)
    }
  }

  if (animationFrameId) {
    window.cancelAnimationFrame(animationFrameId)
  }

  animationFrameId = window.requestAnimationFrame(step)
}

onBeforeUnmount(() => {
  if (animationFrameId) {
    window.cancelAnimationFrame(animationFrameId)
  }
})

watch(
  () => props.statistic.value,
  (newValue) => {
    animate(digitValue.value, newValue, 2000)
  },
  { immediate: true },
)
</script>

<template>
  <div class="statistic-card" data-test-id="statistic-card">
    <div class="statistic-card__oval" :class="`v-oval--${index}`" />

    <div
      class="statistic-card__digits-container"
      data-test-id="statistic-container"
    >
      <div class="statistic-card__title" data-test-id="statistic-title">
        <template v-for="(line, lineIndex) in titleLines" :key="lineIndex">
          <br v-if="lineIndex" />
          {{ line }}
        </template>
      </div>

      <div class="statistic-card__digits" data-test-id="statistic-digits">
        {{ digitValue }}
      </div>

      <div class="statistic-card__unit" data-test-id="statistic-unit">
        {{ statistic.unit }}
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.statistic-card {
  &__oval {
    position: absolute;
    width: 54px;
    height: 54px;
    background-color: var(--ui-color-primary-500);
    border-radius: 50%;
  }

  .v-oval--0 {
    top: -21px;
    left: 9px;
  }

  .v-oval--1 {
    top: -29px;
    left: -11px;
  }

  .v-oval--2 {
    top: -29px;
    left: -11px;
  }

  .v-oval--3 {
    top: 0;
    left: -11px;
  }

  .v-oval--4 {
    top: -9px;
    left: 19px;
  }

  .v-oval--5 {
    top: -20px;
    left: 29px;
  }

  &__digits-container {
    position: relative;
    color: var(--ui-color-supporting-01);
  }

  &__digits {
    font-size: 55px;
    font-weight: 800;
    line-height: 30px;

    @include media($bp-tablet) {
      font-size: 60px;
    }
  }

  &__unit {
    font-size: 25px;
    font-weight: 400;
    line-height: 30px;

    @include media($bp-tablet) {
      font-size: 30px;
    }
  }

  &__title {
    position: absolute;
    top: -66px;
    left: 0;
    width: 159px;
    height: 36px;
    font-size: 15px;
    font-weight: 400;
    line-height: 18px;

    @include media($bp-tablet) {
      top: -57px;
      left: 52px;
      width: 108px;
      height: 28px;
      font-size: 12px;
      font-weight: 800;
      line-height: 14px;
    }
  }
}
</style>
